import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquareIcon, XIcon, MinusIcon, BotIcon, SendIcon, LoaderIcon, ChevronDownIcon, SparklesIcon } from 'lucide-react';
import { useChat } from '../lib/chatContext';
import { ChatMessage } from './ChatMessage';
import { seedData } from '../data/seedData';
import { toast } from 'sonner';
interface OmnipresentChatProps {
  currentAccountId: string | null;
  currentView: string;
  onNavigate?: (view: string, metadata?: any) => void;
  onAction?: (action: string, metadata?: any) => void;
}
export function OmnipresentChat({
  currentAccountId,
  currentView,
  onNavigate,
  onAction
}: OmnipresentChatProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [input, setInput] = useState('');
  const [activeBotType, setActiveBotType] = useState<'internal' | 'external'>('internal');
  const scrollerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    getSession,
    sendMessage,
    isLoading,
    updateContext
  } = useChat();
  // Get current account or fall back to Quad Internal
  const currentAccount = currentAccountId ? seedData.accounts.find(a => a.id === currentAccountId) : seedData.accounts.find(a => a.id === 'acct-quad-internal');
  const session = currentAccount ? getSession(currentAccount.id) : null;
  const loading = currentAccount ? isLoading(currentAccount.id) : false;
  const currentBot = currentAccount?.bots.find(b => b.type === activeBotType);
  // Update context when account or view changes
  useEffect(() => {
    if (currentAccount) {
      updateContext(currentAccount.id, {
        accountId: currentAccount.id,
        accountName: currentAccount.name,
        section: currentView as any
      });
    }
  }, [currentAccount?.id, currentAccount?.name, currentView, updateContext]);
  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollerRef.current && session) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [session?.messages]);
  // Auto-focus input when chat is expanded
  useEffect(() => {
    if (isExpanded && !isMinimized && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isExpanded, isMinimized]);
  // Auto-expand when account changes
  useEffect(() => {
    if (currentAccount && isMinimized) {
      setIsMinimized(false);
      setIsExpanded(true);
    }
  }, [currentAccountId]);
  const handleSendMessage = async () => {
    const text = input.trim();
    if (!text) return;
    const accountToUse = currentAccount || seedData.accounts.find(a => a.id === 'acct-quad-internal');
    if (!accountToUse) {
      toast.error('Chat not available', {
        description: 'Unable to initialize chat. Please refresh the page.'
      });
      return;
    }
    const botToUse = accountToUse.bots.find(b => b.type === activeBotType);
    if (!botToUse?.api_endpoint) {
      toast.error('Chat not available', {
        description: `${botToUse?.name || 'This bot'} is not configured for chat`
      });
      setInput('');
      return;
    }
    const sessionId = accountToUse.id;
    setInput('');
    try {
      await sendMessage(accountToUse.id, botToUse.api_endpoint, sessionId, text);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message', {
        description: 'Please try again or contact support'
      });
    }
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  // Minimized state - floating button
  if (isMinimized) {
    return <motion.button initial={{
      scale: 0,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} onClick={() => setIsMinimized(false)} className="fixed bottom-6 right-6 w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-2xl shadow-blue-600/40 hover:shadow-blue-600/60 transition-all z-[60] flex items-center justify-center group hover:scale-110" title="Open QuadChat" data-id="element-906">
        <MessageSquareIcon className="w-7 h-7 group-hover:scale-110 transition-transform" data-id="element-907" />
        {currentAccount && <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-green-500 border-2 border-slate-950 flex items-center justify-center" data-id="element-908">
            <SparklesIcon className="w-3 h-3 text-white" data-id="element-909" />
          </div>}
      </motion.button>;
  }
  // Collapsed state - thin bar
  if (!isExpanded) {
    return <motion.div initial={{
      x: 400,
      opacity: 0
    }} animate={{
      x: 0,
      opacity: 1
    }} exit={{
      x: 400,
      opacity: 0
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} className="fixed right-0 top-16 bottom-0 w-16 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 border-l border-slate-800 shadow-2xl z-[45] flex flex-col items-center py-6 gap-4" data-id="element-910">
        <button onClick={() => setIsExpanded(true)} className="w-12 h-12 rounded-xl bg-slate-800/60 hover:bg-slate-700 border border-slate-700/50 hover:border-slate-600 text-slate-400 hover:text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center group hover:scale-105" title="Expand QuadChat" data-id="element-911">
          <ChevronDownIcon className="w-5 h-5 -rotate-90 group-hover:scale-110 transition-transform" data-id="element-912" />
        </button>
        {currentAccount ? <button onClick={() => setIsExpanded(true)} className="flex flex-col items-center gap-2 hover:scale-105 transition-transform cursor-pointer" title={`Open chat for ${currentAccount.name}`} data-id="element-913">
            <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          duration: 0.3
        }} data-id="element-914">
              <motion.div animate={{
            scale: [1, 1.05, 1]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-blue-600/20 border-2 border-blue-500/40 shadow-2xl shadow-blue-500/30 p-2 backdrop-blur-sm overflow-hidden hover:border-blue-500/60 transition-colors" data-id="element-915">
                <img src={currentAccount.logo_url} alt={currentAccount.name} className="w-full h-full object-contain relative z-10" data-id="element-916" />
                <motion.div animate={{
              opacity: [0.3, 0.6, 0.3]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-lg" data-id="element-917" />
                <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 shadow-lg shadow-blue-500/50" data-id="element-918">
                  <BotIcon className="w-2 h-2 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-919" />
                </div>
              </motion.div>
            </motion.div>
            <motion.div animate={{
          scale: [1, 1.2, 1],
          opacity: [1, 0.7, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-green-400 to-emerald-400 shadow-xl shadow-green-500/60" data-id="element-920" />
          </button> : <button onClick={() => setIsExpanded(true)} className="flex flex-col items-center gap-2 hover:scale-105 transition-transform cursor-pointer" title="Open QuadChat" data-id="element-921">
            <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          duration: 0.3
        }} data-id="element-922">
              <motion.div animate={{
            scale: [1, 1.05, 1],
            rotate: [0, 5, -5, 0]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600/30 via-purple-600/30 to-blue-600/30 border-2 border-blue-500/50 shadow-2xl shadow-blue-500/30 flex items-center justify-center backdrop-blur-sm hover:border-blue-500/70 transition-colors" data-id="element-923">
                <BotIcon className="w-6 h-6 text-blue-300" data-id="element-924" />
                <motion.div animate={{
              opacity: [0.3, 0.6, 0.3]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute inset-0 rounded-xl bg-gradient-to-br from-blue-400/20 to-purple-400/20" data-id="element-925" />
              </motion.div>
            </motion.div>
            <motion.div animate={{
          scale: [1, 1.2, 1],
          opacity: [1, 0.7, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-green-400 to-emerald-400 shadow-xl shadow-green-500/60" data-id="element-926" />
          </button>}
        <button onClick={() => setIsMinimized(true)} className="mt-auto w-10 h-10 rounded-lg bg-slate-800/60 hover:bg-slate-700 text-slate-400 hover:text-white transition-all flex items-center justify-center" title="Minimize QuadChat" data-id="element-927">
          <MinusIcon className="w-4 h-4" data-id="element-928" />
        </button>
      </motion.div>;
  }
  // Expanded state - full chat panel
  return <motion.div initial={{
    x: 400,
    opacity: 0
  }} animate={{
    x: 0,
    opacity: 1
  }} exit={{
    x: 400,
    opacity: 0
  }} transition={{
    type: 'spring',
    damping: 25,
    stiffness: 200
  }} className="fixed right-0 top-16 bottom-0 w-96 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 border-l border-slate-800 shadow-2xl z-[45] flex flex-col" data-id="element-929">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-slate-950/50" data-id="element-930">
        <div className="flex items-center gap-3" data-id="element-931">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-lg" data-id="element-932">
            <BotIcon className="w-5 h-5 text-white" data-id="element-933" />
          </div>
          <div data-id="element-934">
            <h3 className="text-sm font-bold text-white" data-id="element-935">
              {currentBot?.name || 'QuadChat'}
            </h3>
            <p className="text-xs text-slate-400" data-id="element-936">AI Assistant</p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-937">
          <button onClick={() => setIsExpanded(false)} className="p-2 hover:bg-slate-800/60 rounded-lg transition-all text-slate-400 hover:text-white" title="Collapse" data-id="element-938">
            <ChevronDownIcon className="w-4 h-4 rotate-90" data-id="element-939" />
          </button>
          <button onClick={() => setIsMinimized(true)} className="p-2 hover:bg-slate-800/60 rounded-lg transition-all text-slate-400 hover:text-white" title="Minimize" data-id="element-940">
            <MinusIcon className="w-4 h-4" data-id="element-941" />
          </button>
        </div>
      </div>
      {/* Context Badge */}
      <AnimatePresence mode="wait" data-id="element-942">
        {currentAccount && currentAccountId && <motion.div key={currentAccount.id} initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="px-4 py-3 border-b border-slate-800/50 bg-gradient-to-r from-blue-500/10 to-purple-500/10" data-id="element-943">
            <div className="flex items-center gap-3" data-id="element-944">
              <div className="w-8 h-8 rounded-lg bg-white/95 p-1 shadow-md flex-shrink-0" data-id="element-945">
                <img src={currentAccount.logo_url} alt={currentAccount.name} className="w-full h-full object-contain" data-id="element-946" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-947">
                <p className="text-xs font-semibold text-white truncate" data-id="element-948">
                  Now assisting with
                </p>
                <p className="text-xs text-blue-300 truncate" data-id="element-949">
                  {currentAccount.name}
                </p>
              </div>
              <div className="w-2 h-2 rounded-full bg-green-500 shadow-lg shadow-green-500/50 flex-shrink-0" data-id="element-950" />
            </div>
            {/* Bot Type Switcher */}
            {currentAccount.bots.length > 1 && <div className="flex gap-2 mt-3" data-id="element-951">
                {currentAccount.bots.map(bot => <button key={bot.type} onClick={() => setActiveBotType(bot.type)} className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${activeBotType === bot.type ? 'bg-blue-600 text-white shadow-lg' : 'bg-slate-800/60 text-slate-400 hover:bg-slate-700 hover:text-white'}`} data-id="element-952">
                    {bot.type === 'internal' ? 'Internal' : 'Partner'}
                  </button>)}
              </div>}
          </motion.div>}
      </AnimatePresence>
      {/* Messages */}
      <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent" data-id="element-953">
        {session && session.messages.length === 0 ? <div className="flex flex-col items-center justify-center h-full text-center px-4" data-id="element-954">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600/20 to-purple-600/20 border border-blue-500/30 flex items-center justify-center mb-4" data-id="element-955">
              <SparklesIcon className="w-8 h-8 text-blue-400" data-id="element-956" />
            </div>
            <h3 className="text-sm font-semibold text-white mb-2" data-id="element-957">
              Start a Conversation
            </h3>
            <p className="text-xs text-slate-400 leading-relaxed" data-id="element-958">
              {currentAccountId ? `Ask me anything about ${currentAccount?.name}` : 'Ask me anything about Quad'}
            </p>
          </div> : session?.messages.map(msg => <ChatMessage key={msg.id} message={msg} isUser={msg.role === 'user'} onNavigate={onNavigate} onAction={onAction} accountContext={{
        accountId: currentAccount?.id || 'acct-quad-internal',
        accountName: currentAccount?.name || 'Quad'
      }} data-id="element-959" />)}
        {loading && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex items-center gap-3 text-slate-400 px-3 py-2.5 bg-slate-900/60 border border-slate-800/60 rounded-lg" data-id="element-960">
            <LoaderIcon className="w-4 h-4 animate-spin text-blue-400" data-id="element-961" />
            <span className="text-xs font-medium" data-id="element-962">
              {currentBot?.name || 'QuadChat'} is thinking...
            </span>
          </motion.div>}
      </div>
      {/* Input */}
      <div className="p-4 border-t border-slate-800 bg-slate-950/50" data-id="element-963">
        <div className="flex gap-2" data-id="element-964">
          <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyPress={handleKeyPress} placeholder={currentAccountId && currentAccount ? `Ask about ${currentAccount.name}...` : 'Ask QuadChat anything...'} disabled={loading} className="flex-1 bg-slate-900/80 border border-slate-700/60 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all" data-id="element-965" />
          <button onClick={handleSendMessage} disabled={!input.trim() || loading} className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 disabled:from-slate-700 disabled:to-slate-800 rounded-lg text-white transition-all flex items-center gap-2 shadow-lg shadow-blue-600/30 disabled:shadow-none hover:scale-105 active:scale-95 disabled:hover:scale-100 disabled:cursor-not-allowed" data-id="element-966">
            <SendIcon className="w-4 h-4" data-id="element-967" />
          </button>
        </div>
      </div>
    </motion.div>;
}